import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Grid,
  FormControl,
  Typography,
  Select,
  Input,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  FormControlLabel,
  Switch
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import axios from 'axios';
import { PreorderEventItem } from '../../../actions/preorderEventActions/PreorderEventActionsTypes';
import DeleteIcon from '@material-ui/icons/Delete';
import defaultImage from '../../../images/default-img.jpg';

type ItemImage = {
  id: number;
  url: string;
  key?: string;
};

type UploadFile = {
  fid: number;
  file: File;
};

interface EditItemDialogProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  onUpdate: (updateData: {
    id: number;
    name: string;
    code?: string;
    newCode?: string;
    costPrice?: number;
    minimumQuantity?: number;
    notes?: string;
    labelValuesIdsToAdd?: string;
    labelValuesIdsToRemove?: string;
    price?: number;
    weightPerUnit?: number;
    isFeature?: boolean;
    isPublished?: boolean;
    maxSellQuantity?: number;
    isConversionEnabled?: boolean;
    conversionQuantity?: number;
    itemCodeForConversion?: string;
    imagesIdToRemove?: string;
    files?: UploadFile[];
  }) => void;
  item: PreorderEventItem | null;
  isLoading: boolean;
}

const EditItemDialog: React.FC<EditItemDialogProps> = ({
  open,
  onClose,
  onUpdate,
  item,
  isLoading
}) => {
  const { t } = useTranslation('global');
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    weightPerUnit: '',
    minimumQuantity: '',
    notes: '',
    maxSellQuantity: ''
  });
  
  const [itemLabels, setItemLabels] = useState<number[]>([]);
  const [originalLabels, setOriginalLabels] = useState<number[]>([]);
  const [loadingItemData, setLoadingItemData] = useState(false);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [availableLabelValues, setAvailableLabelValues] = useState<any[]>([]);
  const [fullItemData, setFullItemData] = useState<any>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [isPublished, setIsPublished] = useState<boolean>(true);
  const [images, setImages] = useState<ItemImage[]>([]);
  const [imageFiles, setImageFiles] = useState<UploadFile[]>([]);
  const [imagesIdToRemove, setImagesIdToRemove] = useState<string>('');
  const [enableMaxSellQuantity, setEnableMaxSellQuantity] = useState<boolean>(false);
  
  const [errors, setErrors] = useState({
    name: false,
    sku: false,
    weightPerUnit: false,
    minimumQuantity: false,
    maxSellQuantity: false
  });

  const fileInputId = useMemo(
    () => `edit-item-image-input-${item?.id ?? 'new'}`,
    [item?.id]
  );

  const revokeBlobUrls = (targetImages: ItemImage[]) => {
    targetImages.forEach((image) => {
      if (image?.url && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
    });
  };

  const cleanupTemporaryUrls = () => {
    revokeBlobUrls(images);
  };

  // Function to fetch available labels and values for the item's category
  const fetchAvailableLabels = async (categoryId: number) => {
    try {
      setLoadingLabels(true);
      const apiURL = process.env.REACT_APP_API_URL;
      const token = localStorage.token;

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      };

      // First get all labels for this category
      const labelsResponse = await axios.get(`${apiURL}/user/getAllLabel/${categoryId}`, {
        params: {
          page: 0,
          size: 1000,
          paged: true
        },
        headers: headers
      });

      if (labelsResponse.status === 200 && labelsResponse.data.data && labelsResponse.data.data.content) {
        const labels = labelsResponse.data.data.content;
        const allLabelValues: any[] = [];

        // For each label, fetch its values
        for (const label of labels) {
          const valuesResponse = await axios.get(`${apiURL}/user/getAllLabelValues/${label.id}`, {
            params: {
              page: 0,
              size: 1000,
              paged: true
            },
            headers: headers
          });

          if (valuesResponse.status === 200 && valuesResponse.data.data && valuesResponse.data.data.content) {
            const values = valuesResponse.data.data.content;
            values.forEach((value: any) => {
              allLabelValues.push({
                id: value.id,
                value: value.value,
                labelId: label.id,
                labelName: label.name
              });
            });
          }
        }

        setAvailableLabelValues(allLabelValues);
      }
    } catch (error) {
      setLoadingLabels(false);
    } finally {
      setLoadingLabels(false);
    }
  };

  // Function to fetch full item data by ID
  const fetchItemData = async (itemId: number) => {
    setLoadingItemData(true);
    try {
      const apiURL = process.env.REACT_APP_API_URL;
      const token = localStorage.token;

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      };

      const response = await axios.get(`${apiURL}/seller/getAllItems`, {
        params: { 
          id: itemId,
          isPreorder: true
        },
        headers: headers
      });

      if (response.status === 200 && response.data.data && response.data.data.content && response.data.data.content.length > 0) {
        const itemData = response.data.data.content[0];
        return itemData;
      }
    } catch (error) {
      setLoadingItemData(false);
    } finally {
      setLoadingItemData(false);
    }
    return null;
  };

  useEffect(() => {
    const loadItemData = async () => {
      if (item && open) {
        // Fetch the full item data to get all fields
        const itemData = await fetchItemData(item.id);
        
        if (itemData) {
          // Store the full item data for use in update payload
          setFullItemData(itemData);
          
          // Initialize form with fetched data
          setFormData({
            name: itemData.name || '',
            sku: itemData.code || '',
            weightPerUnit: (itemData.weightPerUnit && itemData.weightPerUnit > 0) ? itemData.weightPerUnit.toString() : '',
            minimumQuantity: (itemData.minimumQuantity && itemData.minimumQuantity > 0) ? itemData.minimumQuantity.toString() : '',
            notes: itemData.notes || '',
            maxSellQuantity: (itemData.maxSellQuantity && itemData.maxSellQuantity > 0) ? itemData.maxSellQuantity.toString() : ''
          });
          
          // Force CKEditor to re-render with new data
          setEditorKey(prev => prev + 1);
          
          setIsPublished(itemData.isPublished !== undefined ? itemData.isPublished : true);
          const normalizedImages = Array.isArray(itemData.images) ? itemData.images.filter(Boolean) : [];
          setImages([...normalizedImages]);
          setImageFiles([]);
          setImagesIdToRemove('');
          
          // Initialize maxSellQuantity checkbox based strictly on a boolean condition
          const hasMaxSellQuantity = itemData.maxSellQuantity > 0;
          setEnableMaxSellQuantity(hasMaxSellQuantity);

          // Initialize labels
          const labelIds: number[] = [];
          if (itemData.labelValues) {
            for (let i = 0; i < itemData.labelValues.length; i++) {
              if (itemData.labelValues[i]) {
                labelIds.push(itemData.labelValues[i].id);
              }
            }
          }
          setItemLabels(labelIds);
          setOriginalLabels([...labelIds]);

          // Fetch available labels for this item's category
          if (itemData.itemType && itemData.itemType.id) {
            await fetchAvailableLabels(itemData.itemType.id);
          }
        } else {
          // Fallback to basic item data and try to use what's available
          setFullItemData(item); // Store basic item data as fallback
          setFormData({
            name: item.name || '',
            sku: item.sku || '',
            weightPerUnit: '',
            minimumQuantity: '',
            notes: '',
            maxSellQuantity: ''
          });
          setItemLabels([]);
          setOriginalLabels([]);
          setIsPublished(true);
          setImages([]);
          setImageFiles([]);
          setImagesIdToRemove('');
          setEnableMaxSellQuantity(false);
        }
        
        setErrors({
          name: false,
          sku: false,
          weightPerUnit: false,
          minimumQuantity: false,
          maxSellQuantity: false
        });
      }
    };

    loadItemData();
  }, [item, open]);

  const handleInputChange = (field: string, value: string) => {
    // For numeric fields, validate input
    if ((field === 'weightPerUnit' || field === 'minimumQuantity' || field === 'maxSellQuantity') && value !== '') {
      const re = /^[0-9\b]+$/;
      if (!re.test(value)) {
        return; // Don't update if not numeric
      }
    }

    // Special handling for numeric fields - don't allow 0 to be displayed
    if ((field === 'weightPerUnit' || field === 'minimumQuantity' || field === 'maxSellQuantity') && value === '0') {
      value = '';
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  const handleLabelChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setItemLabels(event.target.value as number[]);
  };

  const handlePublishedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPublished(event.target.checked);
  };

  const handleMaxSellQuantityToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnableMaxSellQuantity(event.target.checked);
    // Clear the field when disabling
    if (!event.target.checked) {
      setFormData(prev => ({
        ...prev,
        maxSellQuantity: ''
      }));
      // Clear any error for this field
      setErrors(prev => ({
        ...prev,
        maxSellQuantity: false
      }));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (!fileList || fileList.length === 0) {
      return;
    }

    const newImages: ItemImage[] = [];
    const newFiles: UploadFile[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (!file) {
        continue;
      }
      const tempURL = URL.createObjectURL(file);
      const uniqueId = Date.now() + i;
      newImages.push({ id: uniqueId, url: tempURL });
      newFiles.push({ fid: uniqueId, file });
    }

    setImages(prev => [...prev, ...newImages]);
    setImageFiles(prev => [...prev, ...newFiles]);

    // Reset the input so the same file can be re-selected
    event.target.value = '';
  };

  const handleImageDelete = (id: number, url: string) => {
    setImages(prev => prev.filter(image => image.id !== id));
    setImageFiles(prev => prev.filter(file => file.fid !== id));

    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
      return;
    }

    setImagesIdToRemove(prev => {
      if (!prev) {
        return `${id}`;
      }
      const segments = prev.split(',').filter(Boolean);
      if (segments.includes(String(id))) {
        return prev;
      }
      return `${prev},${id}`;
    });
  };

  const validateForm = () => {
    const newErrors = {
      name: !formData.name.trim(),
      sku: !formData.sku.trim(),
      weightPerUnit: false,
      minimumQuantity: false,
      notes: false,
      maxSellQuantity: enableMaxSellQuantity && (!formData.maxSellQuantity.trim() || Number(formData.maxSellQuantity) < 0)
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async () => {
    if (validateForm() && item) {
      // Calculate label changes
      const addedLabels = itemLabels.filter((id: number) => !originalLabels.includes(id));
      const removedLabels = originalLabels.filter((id: number) => !itemLabels.includes(id));
      
      // If we have full item data, use it; otherwise use basic item data
      const itemData = fullItemData || item;
      
      // Check if SKU/code has changed
      const originalCode = itemData.code || item.sku || '';
      const newCode = formData.sku.trim();
      const codeChanged = originalCode !== newCode;
      
      // Build the base update data
      const updateData: any = {
        id: item.id,
        name: formData.name.trim(),
        code: originalCode, // Always send the current/original code
        costPrice: itemData.costPrice || 0,
        minimumQuantity: formData.minimumQuantity ? Number(formData.minimumQuantity) : (itemData.minimumQuantity || 0),
        notes: formData.notes.trim() || '',
        price: itemData.price || itemData.sellPrice || 0,
        weightPerUnit: formData.weightPerUnit ? Number(formData.weightPerUnit) : (itemData.weightPerUnit || 0),
        isFeature: itemData.isFeature || false,
        isPublished,
        maxSellQuantity: enableMaxSellQuantity ? (formData.maxSellQuantity ? Number(formData.maxSellQuantity) : 0) : 0,
        isConversionEnabled: itemData.isConversionEnabled || false,
        conversionQuantity: itemData.conversionQuantity || 0,
        itemCodeForConversion: itemData.itemCodeForConversion || ''
      };

      // Only add newCode if the code has actually changed
      if (codeChanged) {
        updateData.newCode = newCode;
      }

      // Add label changes if any
      if (addedLabels.length > 0) {
        updateData.labelValuesIdsToAdd = addedLabels.toString();
      }
      if (removedLabels.length > 0) {
        updateData.labelValuesIdsToRemove = removedLabels.toString();
      }

      if (imagesIdToRemove) {
        updateData.imagesIdToRemove = imagesIdToRemove;
      }

      if (imageFiles.length > 0) {
        updateData.files = imageFiles;
      }

      onUpdate(updateData);
      cleanupTemporaryUrls();
    }
  };

  const handleCancel = () => {
    cleanupTemporaryUrls();
    onClose(false); // Cancel doesn't refresh
  };

  const handleDialogClose = () => {
    cleanupTemporaryUrls();
    onClose(false); // Dialog close (ESC, backdrop click) doesn't refresh
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('preorderEventDetails.edit-item')}
      </DialogTitle>
      <DialogContent>
        {(loadingItemData || loadingLabels) ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginLeft: 16 }}>
              {loadingItemData ? t('common.loading') : t('common.loading')}...
            </Typography>
          </div>
        ) : (
          <Grid container spacing={2}>
            {/* SKU */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label={t('items.sku')}
                fullWidth
                value={formData.sku}
                onChange={(e) => handleInputChange('sku', e.target.value)}
                error={errors.sku}
                helperText={errors.sku ? t('common.field-required') : ''}
                disabled={isLoading}
              />
            </Grid>

            {/* Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label={t('items.name')}
                fullWidth
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                helperText={errors.name ? t('common.field-required') : ''}
                disabled={isLoading}
              />
            </Grid>

            {/* Weight */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label={t('items.weight')}
                fullWidth
                type="number"
                value={formData.weightPerUnit === '0' ? '' : formData.weightPerUnit}
                onChange={(e) => handleInputChange('weightPerUnit', e.target.value)}
                error={errors.weightPerUnit}
                disabled={isLoading}
                onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
              />
            </Grid>

            {/* Minimum Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label={t('items.minimum-quantity')}
                fullWidth
                type="number"
                value={formData.minimumQuantity === '0' ? '' : formData.minimumQuantity}
                onChange={(e) => handleInputChange('minimumQuantity', e.target.value)}
                error={errors.minimumQuantity}
                disabled={isLoading}
                onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
              />
            </Grid>

            {/* Published Toggle */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <Typography variant="caption" color="initial" style={{ marginBottom: 8 }}>
                  {t('items.state')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPublished}
                      onChange={handlePublishedToggle}
                      color="primary"
                      disabled={isLoading || loadingItemData}
                    />
                  }
                  label={isPublished ? t('items.published') : t('items.not-published')}
                />
              </FormControl>
            </Grid>

            {/* Max Sell Quantity Toggle and Field */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <Typography variant="caption" color="initial" style={{ marginBottom: 8 }}>
                  {t('items.enable-max-sell-quantity')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableMaxSellQuantity}
                      onChange={handleMaxSellQuantityToggle}
                      color="primary"
                      disabled={isLoading || loadingItemData}
                    />
                  }
                  label={enableMaxSellQuantity ? t('cart.yes') : t('cart.no')}
                />
              </FormControl>
            </Grid>

            {/* Max Sell Quantity Number Field - Conditional */}
            {enableMaxSellQuantity && (
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label={t('items.max-sell-quantity')}
                  fullWidth
                  type="number"
                  value={formData.maxSellQuantity === '0' ? '' : formData.maxSellQuantity}
                  onChange={(e) => handleInputChange('maxSellQuantity', e.target.value)}
                  error={errors.maxSellQuantity}
                  helperText={errors.maxSellQuantity ? t('common.field-required') : ''}
                  disabled={isLoading || loadingItemData}
                  onKeyDown={(evt) => evt.key.toLowerCase() === 'e' && evt.preventDefault()}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            )}

            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="caption" color="initial" style={{ marginBottom: 8, display: 'block' }}>
                {t('items.add-image')}
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ position: 'relative', width: 120, height: 120 }}>
                  {images[0] && (
                    <IconButton
                      size="small"
                      onClick={() => handleImageDelete(images[0].id, images[0].url)}
                      style={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#fff', zIndex: 1 }}
                      disabled={isLoading || loadingItemData}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Avatar
                    variant="rounded"
                    alt="Item image"
                    src={images[0] ? images[0].url : defaultImage}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {images.slice(1).map((image) => (
                    <div key={image.id} style={{ position: 'relative' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleImageDelete(image.id, image.url)}
                        style={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#fff', zIndex: 1 }}
                        disabled={isLoading || loadingItemData}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Avatar
                        variant="rounded"
                        alt="Item image"
                        src={image.url}
                        style={{ width: 72, height: 72 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <input
                  accept="image/*"
                  id={fileInputId}
                  type="file"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={isLoading || loadingItemData}
                />
                <label htmlFor={fileInputId}>
                  <Button variant="contained" color="primary" component="span" disabled={isLoading || loadingItemData}>
                    {t('items.add-image')}
                  </Button>
                </label>
              </div>
            </Grid>

            {/* Tags/Labels */}
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <Typography variant="caption" color="initial" style={{ marginBottom: 8 }}>
                  {t('items.labels')}
                </Typography>
                <Select
                  multiple
                  value={itemLabels}
                  onChange={handleLabelChange}
                  input={<Input />}
                  disabled={isLoading}
                  renderValue={(selected) => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(selected as number[]).map((value) => {
                        const labelValue = availableLabelValues.find(lv => lv.id === value);
                        return labelValue ? (
                          <Chip 
                            key={value} 
                            avatar={<Avatar>{labelValue.labelName}</Avatar>} 
                            label={labelValue.value}
                            size="small"
                          />
                        ) : null;
                      })}
                    </div>
                  )}
                >
                  {availableLabelValues.map(labelValue => (
                    <MenuItem key={labelValue.id} value={labelValue.id}>
                      {`${labelValue.labelName}:${labelValue.value}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Typography variant="caption" color="initial" style={{ marginBottom: 8, display: 'block' }}>
                {t('items.notes')}
              </Typography>
              <div style={{ border: '1px solid #ddd', borderRadius: 4 }}>
                {!loadingItemData && !loadingLabels && fullItemData && (
                  <CKEditor
                    key={`ckeditor-${item?.id}-${editorKey}`}
                    editor={ClassicEditor}
                    data={formData.notes}
                    config={{ 
                      toolbar: ['bold', 'italic', 'bulletedList', 'numberedList', 'blockQuote', '|', 'undo', 'redo', '|', 'InsertTable'],
                      placeholder: t('items.notes-placeholder')
                    }}
                    onReady={(editor) => {
                      // Ensure the editor displays the current data
                      editor.setData(formData.notes);
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setFormData(prev => ({
                        ...prev,
                        notes: data
                      }));
                    }}
                    disabled={isLoading}
                  />
                )}
              </div>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          disabled={isLoading || loadingItemData}
          variant="contained"
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              {t('common.updating')}...
            </>
          ) : (
            t('common.update')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditItemDialog;
